import { Injectable, signal, computed } from '@angular/core';
import { getAuth } from 'firebase/auth';
import { getFirestore, doc, setDoc, getDoc } from 'firebase/firestore';

export type Theme = 'dark' | 'light' | 'system';

function setCrewdleCookie(name: string, value: string): void {
  try {
    const date = new Date();
    date.setTime(date.getTime() + 365 * 24 * 60 * 60 * 1000);
    document.cookie = `crewdle_${name}=${encodeURIComponent(value)};expires=${date.toUTCString()};path=/;domain=.crewdle.ai;SameSite=Lax`;
  } catch {}
}

function getCrewdleCookie(name: string): string | null {
  try {
    const prefix = `crewdle_${name}=`;
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const trimmed = cookie.trim();
      if (trimmed.startsWith(prefix)) {
        return decodeURIComponent(trimmed.substring(prefix.length));
      }
    }
  } catch {}
  return null;
}

@Injectable({ providedIn: 'root' })
export class ThemeService {
  theme = signal<Theme>(this.getInitialTheme());
  isDark = computed(() => {
    const t = this.theme();
    if (t === 'system') return window.matchMedia('(prefers-color-scheme: dark)').matches;
    return t === 'dark';
  });

  constructor() {
    this.applyTheme(this.isDark() ? 'dark' : 'light');

    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
      if (this.theme() === 'system') {
        this.applyTheme(window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light');
      }
    });

    // Sync theme from cookie when returning to this tab (cross-platform sync)
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState !== 'visible') return;
      const cookie = getCrewdleCookie('theme') as Theme | null;
      if (cookie && ['dark', 'light', 'system'].includes(cookie) && cookie !== this.theme()) {
        this.setTheme(cookie);
      }
    });
  }

  toggle(): void {
    const next = this.isDark() ? 'light' : 'dark';
    this.setTheme(next);
  }

  setTheme(theme: Theme): void {
    this.theme.set(theme);
    localStorage.setItem('theme', theme);
    setCrewdleCookie('theme', theme);
    this.applyTheme(theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches) ? 'dark' : 'light');
    this.syncToFirestore(theme);
  }

  async loadUserTheme(): Promise<void> {
    try {
      const auth = getAuth();
      const uid = auth.currentUser?.uid;
      if (!uid) return;

      const localTheme = localStorage.getItem('theme');
      const db = getFirestore();
      const snapshot = await getDoc(doc(db, 'users', uid));
      const firestoreTheme = snapshot.exists() ? snapshot.data()['theme'] : null;

      if (localTheme && ['light', 'dark', 'system'].includes(localTheme)) {
        // Local preference exists — push it to Firestore if different
        if (firestoreTheme !== localTheme) {
          await setDoc(doc(db, 'users', uid), { theme: localTheme }, { merge: true });
        }
        setCrewdleCookie('theme', localTheme);
      } else if (firestoreTheme && ['light', 'dark', 'system'].includes(firestoreTheme)) {
        // No local preference — use Firestore value
        this.theme.set(firestoreTheme as Theme);
        localStorage.setItem('theme', firestoreTheme);
        setCrewdleCookie('theme', firestoreTheme);
        this.applyTheme(this.isDark() ? 'dark' : 'light');
      }
    } catch {}
  }

  private getInitialTheme(): Theme {
    const stored = localStorage.getItem('theme') as Theme | null;
    if (stored && ['dark', 'light', 'system'].includes(stored)) return stored;

    const cookie = getCrewdleCookie('theme') as Theme | null;
    if (cookie && ['dark', 'light', 'system'].includes(cookie)) return cookie;

    return 'system';
  }

  private applyTheme(resolved: 'dark' | 'light'): void {
    document.documentElement.classList.toggle('dark', resolved === 'dark');
  }

  private async syncToFirestore(theme: string): Promise<void> {
    try {
      const auth = getAuth();
      const uid = auth.currentUser?.uid;
      if (!uid) return;

      const db = getFirestore();
      await setDoc(doc(db, 'users', uid), { theme }, { merge: true });
    } catch {}
  }
}
